import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { delay, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { ApplianceGroup, Brand, DataRef, DataRefsByKey, Domain, Language, Market } from 'src/app/shared/models/dataref.model';
import { PackArchivesListComponent } from './archive-management/archive-list/pack-archives-list.component';
import { MatSort, Sort } from '@angular/material/sort';
import { PackGenerateArchiveFormComponent } from './archive-management/pack-generate-archive-form/pack-generate-archive-form.component';
import { MatExpansionPanel } from '@angular/material/expansion';
import { LoaderService } from 'src/app/services/loader.service';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormGroup } from '@angular/forms';
import { ContentsSearchFacetsComponent } from '@contents/contents-search/contents-search-facets/contents-search-facets.component';
import { ConfigService, RecipeConfig } from '@services/config.service';
import { RefDataService } from '@services/ref-data.service';
import { SearchService } from '@services/search.service';
import { BINARY_TARGET_DOMAIN, hasTargetDomainForBinaries } from '@packs/utils/pack.utils';
import { BinariesListComponent } from '@shared/components/recipes-form-header/binaries-management-popin/binaries-list/binaries-list.component';
import { RecipesSearchResultComponent } from '@shared/components/recipes-search-result/recipes-search-result.component';
import { PAGE_SIZE } from '@shared/constants/page.constant';
import { BinaryLite } from '@shared/models/binaries/binary-lite.model';
import { PackLite } from '@shared/models/generic/pack-lite.model';
import { FieldFilter, FacetFilter, RecipeSearchResult, Facet, SearchPayload } from '@shared/models/search.model';
import { TablePaginatorComponent } from '@shared/components/data-table-manager/table-paginator/table-paginator.component';

@Component({
  selector: 'app-pack-recipe-tab',
  templateUrl: './pack-recipe-tab.component.html',
  styleUrls: ['./pack-recipe-tab.component.scss'],
  standalone: false,
})
export class PackRecipeTabComponent implements OnChanges, AfterViewInit {
  @Input() formPack: UntypedFormGroup = new UntypedFormGroup({});
  @Input() pack = {} as PackLite;
  @Input() packId = '';
  @Input() isEdit = false;

  @Output() packChanged: EventEmitter<void> = new EventEmitter();
  @Output() publishPack: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('searchResultComponent') searchResultComponent: RecipesSearchResultComponent = {} as RecipesSearchResultComponent;
  @ViewChild('binariesListComponent') binariesListComponent?: BinariesListComponent;
  @ViewChild('archivesListComponent') archivesListComponent?: PackArchivesListComponent;
  @ViewChild('archivesFormComponent') archivesFormComponent?: PackGenerateArchiveFormComponent;
  @ViewChild('archivesPanel') archivesPanel?: MatExpansionPanel;
  @ViewChild('binariesPanel') binariesPanel?: MatExpansionPanel;
  @ViewChild(RecipesSearchResultComponent) resultComponent?: RecipesSearchResultComponent;
  @ViewChild(ContentsSearchFacetsComponent) facetsComponent?: ContentsSearchFacetsComponent;

  brands: Brand[] = [];
  domains: Subject<Domain[]> = new Subject<Domain[]>();
  markets: Market[] = [];
  languages: Language[] = [];
  packs: DataRef[] = [];
  status: DataRef[] = [];

  sort?: Sort = { active: 'modificationDate', direction: 'desc' };
  q?: string;
  fieldFilters: FieldFilter[] = [];
  facetFilters: FacetFilter[] = [];
  pageIndex = 0;
  pageSize = PAGE_SIZE;

  searchResult: Subject<RecipeSearchResult> = new Subject<RecipeSearchResult>();
  selectedAllRecipe: Subject<RecipeSearchResult> = new Subject<RecipeSearchResult>();
  facets: Subject<Facet[]> = new Subject<Facet[]>();
  searchFacetsSemantic: Subject<Facet[]> = new Subject<Facet[]>();

  binaries: BinaryLite[] = [];
  applianceGroupsRefData$?: Observable<DataRefsByKey<ApplianceGroup>>;

  shouldReload$ = new ReplaySubject<void>(1);

  totalElements = 0;

  queryParamLang = '';
  queryParamDomain = '';
  queryParamMarket = '';
  displayArchives = true;
  displayBinaries = true;
  hasTargetDomainForBinaries = false;

  semanticFacetList = [
    'recipeTypology',
    'completeness',
    'equipments',
    'excludedFoods',
    'seasonality',
    'detectedIngredients.semantizationRate',
    'detectedSteps.semantizationRate',
    'energyValue',
    'pnnsPortions.fruitsVegetables',
    'pnnsPortions.meatFishEggs',
    'pnnsPortions.starchy',
    'pnnsPortions.dairy',
    'globalQuality',
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly refDataService: RefDataService,
    private readonly searchService: SearchService,
    private readonly configService: ConfigService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly loaderService: LoaderService,
  ) {}

  ngAfterViewInit(): void {
    this.archivesPanel?.close();
    this.binariesPanel?.close();
    this.cdr.detectChanges();
  }

  initSearch(): void {
    this.fieldFilters = [
      {
        field: 'packs.key',
        values: [this.pack.key],
      },
    ];

    forkJoin(
      ['brands', 'domains', 'markets', 'languages', 'packs', 'statuses'].map((key: string) =>
        this.refDataService.getByKey(key, true, this.pack.domain, this.pack.market),
      ),
    )
      .pipe(
        tap((response) => {
          this.brands = this.refDataService.translateReference(this.refDataService.toArray(response[0]));
          this.markets = this.refDataService.translateReference(this.refDataService.toArray(response[2]));
          this.languages = this.refDataService.translateReference(this.refDataService.toArray(response[3]));
          this.packs = this.refDataService.translateReference(this.refDataService.toArray(response[4]));
          this.status = this.refDataService.translateReference(this.refDataService.toArray(response[5]));
          this.domains.next(this.refDataService.translateReference(this.refDataService.toArray(response[1])));
          this.search();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pack) {
      const pack: PackLite = changes.pack.currentValue;

      this.hasTargetDomainForBinaries = hasTargetDomainForBinaries(this.pack);

      if (pack.key && pack.domain && pack.market && pack.name && !this.applianceGroupsRefData$) {
        this.initSearch();
        const lang = Object.keys(pack.name[pack.market])[0];
        const targetDomainsForBinaries = this.pack.targetDomains?.filter((domain) => domain === BINARY_TARGET_DOMAIN) ?? [];
        this.applianceGroupsRefData$ = this.refDataService
          .getByKey('applianceGroups', true, pack.domain, pack.market, lang, undefined, targetDomainsForBinaries)
          .pipe(
            map((allApplianceGroups) => this.convertRefData<ApplianceGroup>(allApplianceGroups, pack)),
            shareReplay(1),
          );
      }
    }
  }

  selectAllRecipe(): void {
    this.searchService
      .searchRecipe(this.buildSearchPayload(this.resultComponent ? this.resultComponent.sort : undefined), {
        q: this.q,
        size: this.totalElements,
        page: this.pageIndex,
        domain: this.queryParamDomain,
        market: this.queryParamMarket,
        lang: this.queryParamLang,
        groupBy: this.configService.getAppConfig().recipes?.groupBy,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.selectedAllRecipe.next(response);
      });
  }

  buildSearchPayload(sort?: Sort): SearchPayload {
    const payload: SearchPayload = {};
    if (sort?.direction) {
      payload.sort = {
        name: sort.active,
        direction: sort.direction,
      };
    }
    const appConfigRecipes: RecipeConfig | null = this.configService.getAppConfig()?.recipes;
    if (appConfigRecipes) {
      payload.fieldList = appConfigRecipes.fieldList;
      payload.facetList = appConfigRecipes.facetList.map((facet) => ({ name: facet }));
      payload.fieldFilters = this.configService.getRecipeExcludedFiltersInConfig();
    }
    if (this.fieldFilters.length > 0) {
      payload.fieldFilters = [...(payload.fieldFilters ?? []), ...this.fieldFilters];
    }
    if (this.facetFilters.length > 0) {
      payload.facetFilters = [...(payload.facetFilters ?? []), ...this.facetFilters];
    }
    return payload;
  }

  handleSearch(event: { sort: MatSort; paginator: TablePaginatorComponent }): void {
    this.pageIndex = event.paginator.pageIndex;
    this.sort = event.sort;
    this.search();
  }

  search(): void {
    this.searchService
      .searchRecipe(this.buildSearchPayload(this.resultComponent ? this.resultComponent.sort : undefined), {
        q: this.q,
        size: this.pageSize,
        page: this.pageIndex,
        domain: this.pack.domain,
        market: this.pack.market,
        lang: this.queryParamLang,
        groupBy: this.configService.getAppConfig().recipes?.groupBy,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: RecipeSearchResult) => {
        this.totalElements = response.page.totalElements;
        this.searchResult.next(response);
        const facets: Facet[] = [];
        const searchFacetsSemantic: Facet[] = [];
        // reorder
        const allFacets: Facet[] = new Array(this.configService.getAppConfig().recipes?.facetList.length);
        response.facets.forEach((facet) => {
          const facetIndex = this.configService.getAppConfig().recipes?.facetList.indexOf(facet.facet) ?? 0;
          allFacets[facetIndex] = facet;
        });
        // divide in 2 list
        allFacets.forEach((facet) => {
          if (this.semanticFacetList.includes(facet.facet)) {
            searchFacetsSemantic.push(facet);
          } else {
            facets.push(facet);
          }
        });
        this.facets.next(facets);
        this.searchFacetsSemantic.next(searchFacetsSemantic);
      });
  }

  public publishPackEventEmit(): void {
    this.publishPack.emit();
  }

  onCloseDialog(): void {
    this.searchResultComponent.dialogRef?.close();
  }

  onBinaryGenerated(): void {
    this.shouldReload$.next();
    this.refreshPack();
  }

  onDisplayArchivesPanel($event: boolean): void {
    this.displayArchives = $event;
  }

  onDisplayBinariesPanel($event: boolean): void {
    this.displayBinaries = $event;
  }

  // need to wait because some times association/dissociation backend calls are not terminated
  refreshRecipesList(): void {
    this.loaderService.show();
    of({})
      .pipe(
        delay(4000),
        map(() => {
          this.search();
          this.packChanged.emit();
        }),
      )
      .subscribe();
  }

  refreshPack(): void {
    this.packChanged.emit();
  }

  private convertRefData<T extends DataRef>(refData: DataRefsByKey<T>, recipe: PackLite): DataRefsByKey<T> {
    return this.refDataService.toKeyValueObject(
      this.refDataService.translateReference(this.refDataService.toArray(refData), recipe.market, recipe.lang),
    );
  }
}
